export enum TestCaseStatus {
  SUCCESS = 'Success',
  FAILURE = 'Failure',
  WARNING = 'Warning',
  RUNNING = 'Running',
  SKIPPED = 'Skipped'
}

export type TestCase = {
  test_case_id: string;
  test_case_name: string;
  details: string; // Parsed array of messages (after JSON parsing)
  time_taken: string; // Formatted as HH:MM:SS (after conversion from seconds)
  result: TestCaseStatus;
  log_url?: string;
};

export type TestSummaryStatistics = {
  total_executed: number;
  success_count: number;
  failed_count: number;
  skipped_count: number;
  success_rate: number;
  failed_rate: number;
  skipped_rate: number;
};

export type TestSummary = {
  test_execution_id: string;
  // labels on top of the summary card
  test_collection_name: string;
  workflow_id: string;
  product_version: string;
  product_number: string;
  start_timestamp: string;
  duration: string;
  // kpis on the summary card
  statistics: TestSummaryStatistics;
  // Note: test_cases are fetched separately via paginated API
};

// Helper function to format duration to HH:MM:SS format
// Input can be in format like "25m 30s" or already in HH:MM:SS
export function formatDuration(duration: string): string {
  // If already in HH:MM:SS format, return as is
  if (/^\d{2}:\d{2}:\d{2}$/.test(duration)) {
    return duration;
  }

  // Parse formats like "25m 30s" or "1h 25m 30s"
  let hours = 0;
  let minutes = 0;
  let seconds = 0;

  const hourMatch = duration.match(/(\d+)h/);
  const minuteMatch = duration.match(/(\d+)m/);
  const secondMatch = duration.match(/(\d+)s/);

  if (hourMatch) hours = parseInt(hourMatch[1], 10);
  if (minuteMatch) minutes = parseInt(minuteMatch[1], 10);
  if (secondMatch) seconds = parseInt(secondMatch[1], 10);

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Format time taken from seconds (as string) to HH:MM:SS format
 * @param secondsString - Time in seconds as string, e.g. "13.527556"
 * @returns Formatted time as HH:MM:SS, e.g. "00:00:13"
 */
export function formatTimeTaken(secondsString: string): string {
  try {
    const totalSeconds = Math.floor(parseFloat(secondsString));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  } catch (error) {
    console.error('Error formatting time taken:', error);
    return '00:00:00';
  }
}

/**
 * Parse test case details from JSON string to array
 * @param detailsJsonString - JSON string, e.g. '["Message 1", "Message 2"]'
 * @returns Array of message strings, or empty array if parsing fails
 */
export function parseTestCaseDetails(detailsJsonString: string): string[] {
  try {
    if (!detailsJsonString || detailsJsonString.trim() === '') {
      return [];
    }
    const parsed = JSON.parse(detailsJsonString);
    if (Array.isArray(parsed)) {
      return parsed.map((item: {type:string; content:string}) => `${item.type}: ${item.content}`);
    }
    return [];
  } catch (error) {
    console.error('Error parsing test case details:', error);
    return [];
  }
}
